// ─────────────────────────────────────────────────────────────────────
// Access Management → Account Security (Round 12, decision D4)
//
// Password administration WITHOUT passwords: readiness of every account for
// the new login rules, and per-employee actions that only ever send a link
// or force a fresh setup. Visible to IT Admin and holders of the PROTECTED
// permission "Account Security - Administer" (module admin.accountSecurity).
// Round 13 adds the bulk go-live send on top of this tab.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Input, Button, Badge, Spinner, Table, Modal, ModalHeader, ModalBody, ModalFooter, Label } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../api";
import useAuth from "../../Personal Attendance/useAuth";
import BulkSetupSendPanel from "./BulkSetupSendPanel";   // Round 13

export const CLASS_LABELS = {
    Ready:        { label: "Ready",          color: "success",   help: "Active, e-mail on file, password already in the new (BCrypt) format." },
    NeedsSetup:   { label: "Needs setup",    color: "warning",   help: "Active with an e-mail, but no password or a legacy-format password — send a setup link." },
    MissingEmail: { label: "Missing e-mail", color: "danger",    help: "Active but no usable e-mail address — add one in HR before a link can be sent." },
    Inactive:     { label: "Inactive",       color: "secondary", help: "Status is not A — cannot log in and never receives links." },
    DataConflict: { label: "Data conflict",  color: "danger",    help: "The same e-mail is on more than one ACTIVE account (a shared or placeholder mailbox). A link sent there could set this employee's password from someone else's mailbox, so e-mail sends are refused. Fix: enter the employee's own e-mail in HR, or (IT Admin) use Get link for an in-person setup." },
};

export async function fetchReadiness() {
    const res = await api.get("/admin/account-security/readiness");
    return res.data;
}

export default function AccountSecurityTab() {
    const auth = useAuth();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [cls, setCls] = useState("all");
    const [busyId, setBusyId] = useState(null);
    const [selected, setSelected] = useState(() => new Set());   // Round 13: row selection for bulk send
    const [issued, setIssued] = useState(null);                  // Round 16: { row, link, expiresUtc, message }
    const [copied, setCopied] = useState(false);                 // Round 16: "Copied" state of the link/code modal
    // Round 20: on-demand master password (IT Admin only) — generate, shown once, 60 min, revoke.
    const [mp, setMp] = useState(null);
    const [mpIssued, setMpIssued] = useState(null);   // { password, expiresUtc } shown once
    const [mpBusy, setMpBusy] = useState(false);
    const [mpCopied, setMpCopied] = useState(false);
    const [clock, setClock] = useState(Date.now());
    const loadMp = async () => {
        try { const r = await api.get("/admin/account-security/master-password/status"); setMp(r.data); }
        catch (e) { setMp({ error: e?.response?.status ? `HTTP ${e.response.status}` : "no response" }); }
    };
    useEffect(() => {
        if (!auth.isItAdmin) return undefined;
        loadMp();
        const t = setInterval(() => { setClock(Date.now()); }, 15000);
        return () => clearInterval(t);
        
    }, [auth.isItAdmin]);
    const issueMaster = async () => {
        if (!window.confirm(`Generate a temporary master password?\n\nIt opens ANY non-IT-Admin account for the next ${mp?.minutesValid ?? 60} minutes, then stops working by itself. It is shown once. The login and every action done with it are recorded in the audit trail together with your name as the issuer.`)) return;
        setMpBusy(true); setMpCopied(false);
        try {
            const r = await api.post("/admin/account-security/master-password/issue");
            setMpIssued(r.data); await loadMp();
        } catch (e) { toast.error(e?.response?.data?.error || "Could not generate the master password."); }
        finally { setMpBusy(false); }
    };
    const revokeMaster = async () => {
        if (!window.confirm("Revoke the master password now? It will stop working immediately.")) return;
        setMpBusy(true);
        try { const r = await api.post("/admin/account-security/master-password/revoke"); toast.info(r?.data?.message || "Revoked."); setMpIssued(null); await loadMp(); }
        catch (e) { toast.error(e?.response?.data?.error || "Could not revoke."); }
        finally { setMpBusy(false); }
    };
    const copyMaster = async () => {
        try { await navigator.clipboard.writeText(mpIssued.password); setMpCopied(true); }
        catch { toast.info("Select the password and copy it manually."); }
    };
    const mpMinutesLeft = mp?.active && mp.expiresUtc ? Math.max(0, Math.round((new Date(mp.expiresUtc).getTime() - clock) / 60000)) : 0;

    // Round 16: create a setup link and show it once (IT Admin only) — for
    // employees with no e-mail or a shared mailbox. Hand it over in person.
    const getLink = async (r) => {
        if (!window.confirm(`Create a password SETUP link for ${r.name || r.empId} and show it here?\n\nIt is valid 72 hours, can be used once, and is NOT e-mailed — give it to the employee in person (or through a channel you trust).`)) return;
        setBusyId(r.empId);
        try {
            const res = await api.post(`/admin/account-security/${encodeURIComponent(r.empId)}/issue-setup-link`);
            setIssued({ row: r, ...res.data }); setCopied(false);
            await load();
        } catch (e) {
            const d = e?.response?.data;
            toast.error(d?.error || d?.message || "Could not create the link.");
        } finally { setBusyId(null); }
    };
    const copyLink = async () => {
        try { await navigator.clipboard.writeText(issued.link || issued.code || ""); setCopied(true); }
        catch { toast.info("Select the text and copy it manually."); }
    };
    // Round 17: 8-digit setup code for employees without e-mail (IT Admin only).
    const getCode = async (r) => {
        if (!window.confirm(`Create a SETUP CODE for ${r.name || r.empId} (Emp ID ${r.empId})?\n\nThe employee enters Emp ID + code + a new password on the "I have a setup code" page (link on the sign-in page). Single use; expires soon; only works with this Emp ID.`)) return;
        setBusyId(r.empId);
        try {
            const res = await api.post(`/admin/account-security/${encodeURIComponent(r.empId)}/issue-setup-code`);
            setIssued({ row: r, ...res.data }); setCopied(false);
            await load();
        } catch (e) {
            const d = e?.response?.data;
            toast.error(d?.error || d?.message || "Could not create the code.");
        } finally { setBusyId(null); }
    };

    const load = async () => {
        setLoading(true); setError(null);
        try { setReport(await fetchReadiness()); }
        catch (e) { setError(e?.response?.status === 403 ? "You do not hold Account Security - Administer." : "Could not load account readiness."); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // Round 17: per-column filters (text = contains; selects = exact) and paging.
    const EMPTY_FILTERS = { empId: "", name: "", department: "", roleName: "", empStatus: "", email: "", passwordFormat: "", openLink: "", lastLogin: "" };
    const [colFilters, setColFilters] = useState(EMPTY_FILTERS);
    const setCol = (k, v) => { setColFilters((f) => ({ ...f, [k]: v })); setPage(1); };
    const [pageSize, setPageSize] = useState(50);
    const [page, setPage] = useState(1);
    const has = (v, q) => String(v ?? "").toLowerCase().includes(q.toLowerCase());

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const f = colFilters;
        return (report?.rows || []).filter((r) => {
            if (cls !== "all" && r.classification !== cls) return false;
            if (f.empId && !has(r.empId, f.empId)) return false;
            if (f.name && !has(r.name, f.name)) return false;
            if (f.department && !has(r.department, f.department)) return false;
            if (f.roleName && !has(r.roleName || "Unassigned", f.roleName)) return false;
            if (f.empStatus && String(r.empStatus || "") !== f.empStatus) return false;
            if (f.email && !has(r.email, f.email)) return false;
            if (f.passwordFormat && r.passwordFormat !== f.passwordFormat) return false;
            if (f.openLink === "yes" && !r.hasOpenLink) return false;
            if (f.openLink === "no" && r.hasOpenLink) return false;
            if (f.lastLogin === "never" && r.lastLoginUtc) return false;
            if (f.lastLogin === "ever" && !r.lastLoginUtc) return false;
            if (!q) return true;
            return [r.empId, r.name, r.department, r.roleName, r.email, r.empStatus].filter(Boolean).join(" ").toLowerCase().includes(q);
        });
    }, [report, search, cls, colFilters]);

    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const pageRows = useMemo(() => rows.slice((safePage - 1) * pageSize, safePage * pageSize), [rows, safePage, pageSize]);
    const statuses = useMemo(() => Array.from(new Set((report?.rows || []).map((r) => r.empStatus).filter(Boolean))).sort(), [report]);
    const roleNames = useMemo(() => Array.from(new Set((report?.rows || []).map((r) => r.roleName || "Unassigned"))).sort(), [report]);

    const act = async (r, kind) => {
        const labels = { "send-setup": "Send a password SETUP link (valid 72 h)", "send-reset": "Send a password RESET link (valid 30 min)",
                         "force-reset": "FORCE a reset: the current password is removed immediately, every session is signed out, and a setup link is sent" };
        if (!window.confirm(`${labels[kind]} to ${r.name || r.empId} <${r.email || "no e-mail"}>?`)) return;
        setBusyId(r.empId);
        try {
            const res = await api.post(`/admin/account-security/${encodeURIComponent(r.empId)}/${kind}`);
            toast.success(res?.data?.message || "Done.");
            await load();
        } catch (e) {
            const d = e?.response?.data;
            toast.error(d?.message || d?.error || "The action failed.");
        } finally { setBusyId(null); }
    };

    if (loading && !report) return <div className="text-center py-4"><Spinner size="sm" /> Loading account readiness…</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;

    const counts = report?.counts || {};
    return (
        <>
            {/* Only a warning when the live server would build wrong links; silent otherwise. */}
            {!report.isDevelopment && report.frontendBaseUrl && /localhost|127\.0\.0\.1|^http:\/\//i.test(report.frontendBaseUrl) && (
                <div className="alert alert-danger py-2 small">
                    <i className="ri-error-warning-line me-1" />
                    Links would be built from <code>{report.frontendBaseUrl}</code> — set <code>Frontend:BaseUrl</code> on the API server to the https dashboard address before sending anything.
                </div>
            )}
            {(!report.emailConfigured || !report.frontendBaseUrlConfigured) && (
                <div className="alert alert-warning py-2">
                    {!report.emailConfigured && <div>E-mail (SMTP / SendGrid) does not look configured on the API server — sends will fail.</div>}
                    {!report.frontendBaseUrlConfigured && <div><code>Frontend:BaseUrl</code> is not configured on the API server — links cannot be built.</div>}
                </div>
            )}
            <div className="d-flex flex-wrap gap-2 mb-3">
                {Object.keys(CLASS_LABELS).map((k) => (
                    <Button key={k} size="sm" color={cls === k ? CLASS_LABELS[k].color : "light"} onClick={() => { setCls(cls === k ? "all" : k); setPage(1); }} title={CLASS_LABELS[k].help}>
                        {CLASS_LABELS[k].label} <Badge color="light" className="text-dark ms-1">{counts[k] || 0}</Badge>
                    </Button>
                ))}
                <Button size="sm" color={cls === "all" ? "primary" : "light"} onClick={() => { setCls("all"); setPage(1); }}>All <Badge color="light" className="text-dark ms-1">{counts.Total || 0}</Badge></Button>
            </div>
            {/* Round 20: on-demand master password (IT Admin only) */}
            {auth.isItAdmin && (
                <div className={`border rounded p-3 mb-3 ${mp?.active ? "border-danger bg-danger-subtle" : "bg-light-subtle"}`}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                            <i className="ri-key-line me-1" />
                            <strong>Master password</strong>{" "}
                            {mp == null ? <span className="text-muted small">checking…</span>
                             : mp.error ? <span className="text-danger small">status unavailable ({mp.error}{mp.error === "HTTP 404" ? " — rebuild/redeploy the API" : ""})</span>
                             : mp.active
                                ? <span className="text-danger fw-semibold small">ACTIVE — {mpMinutesLeft} min left (until {new Date(mp.expiresUtc).toLocaleTimeString()}), issued by {mp.issuedByName || mp.issuedBy}</span>
                                : <span className="text-muted small">none active</span>}
                            <div className="small text-muted mt-1">
                                Generate → a random password is shown once → it opens any non-IT-Admin account for {mp?.minutesValid ?? 60} minutes, then stops by itself.
                                Sign-in page: employee's Emp ID + this password. The session is marked, lasts {mp?.sessionMinutes ?? 60} minutes, cannot reach admin pages, and every action is audited with your name as the issuer.
                                <strong> It does not open IT Admin accounts (including yours)</strong> — test it with a non-admin Emp ID.
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <Button color="danger" size="sm" disabled={mpBusy || !!mp?.error} onClick={issueMaster}>
                                {mpBusy ? <Spinner size="sm" /> : <><i className="ri-flashlight-line me-1" />{mp?.active ? "Generate a new one" : "Generate master password"}</>}
                            </Button>
                            {mp?.active && <Button color="light" size="sm" disabled={mpBusy} onClick={revokeMaster}><i className="ri-close-circle-line me-1" />Revoke now</Button>}
                        </div>
                    </div>
                    {mpIssued && (
                        <div className="mt-3 text-center">
                            <div className="small text-muted">Shown once — copy it now</div>
                            <div style={{ fontFamily: "monospace", fontSize: 30, letterSpacing: 3, fontWeight: 600 }}>{mpIssued.password}</div>
                            <div className="small text-muted">valid until {new Date(mpIssued.expiresUtc).toLocaleTimeString()} · not case-sensitive</div>
                            <Button color="primary" size="sm" className="mt-2" onClick={copyMaster}><i className="ri-file-copy-line me-1" />{mpCopied ? "Copied" : "Copy"}</Button>
                            <Button color="light" size="sm" className="mt-2 ms-2" onClick={() => setMpIssued(null)}>Hide</Button>
                        </div>
                    )}
                </div>
            )}

            {/* Round 13: go-live bulk send (all "Needs setup", or the ticked rows) */}
            <div className="border rounded p-2 mb-3 bg-light-subtle">
                <BulkSetupSendPanel selectedEmpIds={Array.from(selected)} onFinished={load} />
                {selected.size > 0 && (
                    <div className="small text-muted mt-1">
                        {selected.size} row(s) ticked — the bulk send targets only those.{" "}
                        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setSelected(new Set())}>Clear selection</button>
                    </div>
                )}
            </div>
            <Row className="g-2 align-items-center mb-3">
                <Col md={8}><Input type="search" bsSize="sm" placeholder="Search Emp ID, name, department, role, e-mail…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} /></Col>
                <Col md={4} className="text-md-end">
                    <span className="text-muted small me-2">{rows.length} match · generated {new Date(report.generatedUtc).toLocaleTimeString()}</span>
                    <Input type="select" bsSize="sm" style={{ width: 110, display: "inline-block" }} className="me-2" value={pageSize}
                           onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} title="Rows per page">
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                        <option value={100000}>All rows</option>
                    </Input>
                    {(Object.values(colFilters).some(Boolean) || search) && (
                        <Button color="light" size="sm" className="me-2" onClick={() => { setColFilters(EMPTY_FILTERS); setSearch(""); setPage(1); }}>
                            <i className="ri-filter-off-line align-bottom me-1" />Clear filters
                        </Button>
                    )}
                    <Button color="light" size="sm" onClick={load} disabled={loading}><i className="ri-refresh-line align-bottom me-1" />Refresh</Button>
                </Col>
            </Row>
            <Table responsive size="sm" className="align-middle">
                <thead>
                    <tr style={{ fontSize: 12, color: "var(--ssw-fg-2, #8a93a8)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                        <th style={{ width: 28 }}>
                            <input type="checkbox" title="Select all shown rows"
                                   checked={pageRows.length > 0 && pageRows.every((r) => selected.has(r.empId))}
                                   title="Select all rows on this page"
                                   onChange={(e) => setSelected((prev) => { const n = new Set(prev); pageRows.forEach((r) => e.target.checked ? n.add(r.empId) : n.delete(r.empId)); return n; })} />
                        </th>
                        <th>Emp ID</th><th>Name</th><th>Department</th><th>Role</th><th>Status</th><th>E-mail</th><th>Password</th><th>Open link</th><th>Last login</th><th>Readiness</th><th className="text-end">Actions</th>
                    </tr>
                    {/* Round 17: column-wise filters */}
                    <tr>
                        <th />
                        <th><Input bsSize="sm" placeholder="filter" value={colFilters.empId} onChange={(e) => setCol("empId", e.target.value)} /></th>
                        <th><Input bsSize="sm" placeholder="filter" value={colFilters.name} onChange={(e) => setCol("name", e.target.value)} /></th>
                        <th><Input bsSize="sm" placeholder="filter" value={colFilters.department} onChange={(e) => setCol("department", e.target.value)} /></th>
                        <th>
                            <Input type="select" bsSize="sm" value={colFilters.roleName} onChange={(e) => setCol("roleName", e.target.value)}>
                                <option value="">all</option>
                                {roleNames.map((n) => <option key={n} value={n}>{n}</option>)}
                            </Input>
                        </th>
                        <th>
                            <Input type="select" bsSize="sm" value={colFilters.empStatus} onChange={(e) => setCol("empStatus", e.target.value)}>
                                <option value="">all</option>
                                {statuses.map((st) => <option key={st} value={st}>{st}</option>)}
                            </Input>
                        </th>
                        <th><Input bsSize="sm" placeholder="filter" value={colFilters.email} onChange={(e) => setCol("email", e.target.value)} /></th>
                        <th>
                            <Input type="select" bsSize="sm" value={colFilters.passwordFormat} onChange={(e) => setCol("passwordFormat", e.target.value)}>
                                <option value="">all</option><option value="none">none</option><option value="legacy">legacy</option><option value="bcrypt">bcrypt</option>
                            </Input>
                        </th>
                        <th>
                            <Input type="select" bsSize="sm" value={colFilters.openLink} onChange={(e) => setCol("openLink", e.target.value)}>
                                <option value="">all</option><option value="yes">open</option><option value="no">none</option>
                            </Input>
                        </th>
                        <th>
                            <Input type="select" bsSize="sm" value={colFilters.lastLogin} onChange={(e) => setCol("lastLogin", e.target.value)}>
                                <option value="">all</option><option value="never">never</option><option value="ever">has logged in</option>
                            </Input>
                        </th>
                        <th className="small text-muted">use the chips above</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={12} className="text-center text-muted py-4 small">No accounts match.</td></tr>
                    ) : pageRows.map((r) => {
                        const c = CLASS_LABELS[r.classification] || { label: r.classification, color: "light" };
                        const canSend = r.classification === "Ready" || r.classification === "NeedsSetup";
                        const lockedItAdmin = r.isItAdmin && !auth.isItAdmin;
                        const isSelf = String(r.empId).toLowerCase() === String(auth.empId || "").toLowerCase();
                        const disabled = busyId === r.empId || !canSend || lockedItAdmin;
                        return (
                            <tr key={r.empId}>
                                <td>
                                    <input type="checkbox" checked={selected.has(r.empId)}
                                           onChange={(e) => setSelected((prev) => { const n = new Set(prev); if (e.target.checked) n.add(r.empId); else n.delete(r.empId); return n; })} />
                                </td>
                                <td><code>{r.empId}</code>{r.isItAdmin && <i className="ri-shield-keyhole-line text-primary ms-1" title="IT Admin" />}</td>
                                <td>{r.name || <span className="text-muted">—</span>}</td>
                                <td>{r.department || <span className="text-muted">—</span>}</td>
                                <td>{r.roleName || <span className="text-muted">Unassigned</span>}</td>
                                <td><Badge color="light" className="text-dark">{r.empStatus || "?"}</Badge></td>
                                <td className="small">{r.email || <span className="text-danger">none</span>}</td>
                                <td><Badge color={r.passwordFormat === "bcrypt" ? "success" : r.passwordFormat === "legacy" ? "warning" : "secondary"} pill>{r.passwordFormat}</Badge></td>
                                <td className="small">{r.hasOpenLink ? `until ${new Date(r.linkExpiresUtc).toLocaleString()}` : <span className="text-muted">—</span>}</td>
                                <td className="small">{r.lastLoginUtc ? new Date(r.lastLoginUtc).toLocaleString() : <span className="text-muted">never</span>}</td>
                                <td>
                                    <Badge color={c.color} pill title={c.help}>{c.label}</Badge>
                                    {r.classification === "DataConflict" && Array.isArray(r.conflictsWith) && r.conflictsWith.length > 0 && (
                                        <div className="small text-muted" style={{ maxWidth: 220 }}
                                             title={`Also used by: ${r.conflictsWith.join(", ")}`}>
                                            shared with {r.conflictsWith.length} other active account{r.conflictsWith.length === 1 ? "" : "s"}
                                            {" "}({r.conflictsWith.slice(0, 3).join(", ")}{r.conflictsWith.length > 3 ? ", …" : ""})
                                        </div>
                                    )}
                                </td>
                                <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                    {lockedItAdmin ? (
                                        <span className="text-muted small fst-italic"><i className="ri-lock-2-line me-1" />IT Admin only</span>
                                    ) : (
                                        <>
                                            {canSend ? (
                                                <>
                                                    <Button color="link" size="sm" className="p-1" disabled={disabled} onClick={() => act(r, "send-setup")} title="Send setup link (72 h)"><i className="ri-mail-send-line" /> Setup</Button>
                                                    <Button color="link" size="sm" className="p-1" disabled={disabled} onClick={() => act(r, "send-reset")} title="Send reset link (30 min)"><i className="ri-lock-unlock-line" /> Reset</Button>
                                                    <Button color="link" size="sm" className="p-1 text-danger" disabled={disabled || isSelf} onClick={() => act(r, "force-reset")} title={isSelf ? "Use Change password on My Profile for your own account." : "Force reset: remove password, sign out everywhere, send setup link"}><i className="ri-alert-line" /> Force</Button>
                                                </>
                                            ) : (
                                                <span className="text-muted small fst-italic me-1" title={c.help}>
                                                    {r.classification === "Inactive" ? "not active" : "cannot e-mail"}
                                                </span>
                                            )}
                                            {/* Round 16: in-person setup when e-mail is impossible (IT Admin only) */}
                                            {auth.isItAdmin && r.classification !== "Inactive" && (
                                                <Button color="link" size="sm" className="p-1" disabled={busyId === r.empId} onClick={() => getLink(r)}
                                                        title="Create a 72-h setup link and show it here (not e-mailed) — for an in-person setup">
                                                    <i className="ri-link" /> Get link
                                                </Button>
                                            )}
                                            {auth.isItAdmin && r.classification !== "Inactive" && (
                                                <Button color="link" size="sm" className="p-1" disabled={busyId === r.empId} onClick={() => getCode(r)}
                                                        title="Create an 8-digit setup code (shown here, not e-mailed) — the employee types Emp ID + code + new password on the sign-in page's 'I have a setup code' link">
                                                    <i className="ri-key-2-line" /> Get code
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
            {/* Round 17: pager */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2">
                <span className="small text-muted">
                    {rows.length === 0 ? "0 rows" : `Rows ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, rows.length)} of ${rows.length}`}
                    {selected.size > 0 && ` · ${selected.size} selected`}
                </span>
                <div className="d-flex align-items-center gap-1">
                    <Button color="light" size="sm" disabled={safePage <= 1} onClick={() => setPage(1)} title="First"><i className="ri-skip-back-mini-line" /></Button>
                    <Button color="light" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}><i className="ri-arrow-left-s-line" /> Prev</Button>
                    <span className="small mx-2">Page {safePage} / {pageCount}</span>
                    <Button color="light" size="sm" disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}>Next <i className="ri-arrow-right-s-line" /></Button>
                    <Button color="light" size="sm" disabled={safePage >= pageCount} onClick={() => setPage(pageCount)} title="Last"><i className="ri-skip-forward-mini-line" /></Button>
                </div>
            </div>

            {/* Round 16/17: the issued link or code is shown ONCE */}
            <Modal isOpen={!!issued} toggle={() => setIssued(null)}>
                <ModalHeader toggle={() => setIssued(null)}>{issued?.code ? "Setup code" : "Setup link"} for {issued?.row?.name || issued?.row?.empId}</ModalHeader>
                <ModalBody>
                    <p className="small mb-2">{issued?.message}</p>
                    {issued?.code ? (
                        <div className="text-center my-3">
                            <div style={{ fontFamily: "monospace", fontSize: 34, letterSpacing: 6, fontWeight: 600 }}>
                                {issued.code.slice(0, 4)} {issued.code.slice(4)}
                            </div>
                            <div className="small text-muted">Emp ID <code>{issued?.row?.empId}</code> · page: <code>/setup-code</code> (link "I have a setup code" on the sign-in page)</div>
                        </div>
                    ) : (
                        <Input type="textarea" readOnly rows={3} value={issued?.link || ""} onFocus={(e) => e.target.select()} style={{ fontFamily: "monospace", fontSize: 12 }} />
                    )}
                    <p className="small text-muted mt-2 mb-0">
                        Valid until {issued?.expiresUtc ? new Date(issued.expiresUtc).toLocaleString() : "—"} · single use · this window is the only place it is shown;
                        closing it means creating a new one (which replaces this one). Give it to the employee in person or through a channel you trust — never in a shared mailbox.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={copyLink}><i className="ri-file-copy-line me-1" />{copied ? "Copied" : (issued?.code ? "Copy code" : "Copy link")}</Button>
                    <Button color="light" onClick={() => setIssued(null)}>Close</Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
